/**
 * Hook for managing conversation state
 * Consolidates all useState declarations for the Conversation component
 */

import { useState } from 'react';

interface Message {
  id?: string;
  from: string;
  text: string;
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email';
  _createdDate: Date;
  isFromCustomer?: boolean;
  isSystemMessage?: boolean;
  avatarUrl?: string;
  extractedStaffInfo?: { staffName: string; avatarUrl?: string };
  invoicePaidData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; icon: string };
  newOrderData?: { title: string; textLines?: string[]; buttons?: Array<{ label?: string; launchUrl?: string }>; imageUrl?: string };
  media?: Array<{
    url?: string;
    type: 'image' | 'video' | 'file' | 'audio' | 'icon';
    name?: string;
    iconName?: string;
    duration?: number;
  }>;
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderType?: string;
  senderStaffId?: string;
}

export interface ConversationState {
  // Message management
  messages: Message[];
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  message: string;
  setMessage: (message: string) => void;
  subject: string;
  setSubject: (subject: string) => void;

  // Conversation metadata
  displayName: string;
  setDisplayName: (name: string) => void;
  contactAvatarUrl: string | undefined;
  setContactAvatarUrl: (url: string | undefined) => void;
  contactId: string | undefined;
  setContactId: (id: string | undefined) => void;
  realId: string;
  setRealId: (id: string) => void;
  isBlankShellActive: boolean;
  setIsBlankShellActive: (active: boolean) => void;

  // Channel and preferences
  channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email';
  setChannel: (channel: 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email') => void;

  // Loading states
  loading: boolean;
  setLoading: (loading: boolean) => void;
  loadingOlderMessages: boolean;
  setLoadingOlderMessages: (loading: boolean) => void;
  hasMoreOlderMessages: boolean;
  setHasMoreOlderMessages: (hasMore: boolean) => void;

  // Pagination
  paginationCursor: string | undefined;
  setPaginationCursor: (cursor: string | undefined) => void;

  // Modal states
  showOptionsModal: boolean;
  setShowOptionsModal: (show: boolean) => void;
  selectedImage: { url?: string; name?: string; type: 'image' | 'video' | 'file' | 'icon'; iconName?: string } | null;
  setSelectedImage: (image: { url?: string; name?: string; type: 'image' | 'video' | 'file' | 'icon'; iconName?: string } | null) => void;
  showImageModal: boolean;
  setShowImageModal: (show: boolean) => void;
  showAddModal: boolean;
  setShowAddModal: (show: boolean) => void;
  showTemplatesModal: boolean;
  setShowTemplatesModal: (show: boolean) => void;
}

/**
 * Initialize all conversation state
 * @param urlId - Initial conversation ID from URL
 * @returns ConversationState with all state setters
 */
export const useConversationState = (
  urlId: string,
  initialChannel?: 'chat' | 'email' | 'sms',
  // Seeds so the header shows the contact's name and avatar from the very first render,
  // instead of an empty header that fills in a frame later (when the caller already knows them).
  initialDisplayName?: string,
  initialContactAvatarUrl?: string,
): ConversationState => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [displayName, setDisplayName] = useState<string>(initialDisplayName ?? '');
  const [contactAvatarUrl, setContactAvatarUrl] = useState<string | undefined>(initialContactAvatarUrl);
  const [contactId, setContactId] = useState<string | undefined>(undefined);
  const [realId, setRealId] = useState<string>(urlId || '');
  const [isBlankShellActive, setIsBlankShellActive] = useState(false);

  const [channel, setChannel] = useState<'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email'>(() => {
    // An explicit caller intent (ContactsListModal's Message/Email slider) wins
    // over the last-used-channel default so the composer opens on the right
    // channel from the first render — before ConversationInputFooter's
    // IonSegment fires its mount-time onIonChange, which would otherwise clobber
    // a channel set later via an effect.
    if (initialChannel) return initialChannel;
    try {
      const saved = localStorage.getItem('lastUsedChannel');
      return (saved === 'chat' || saved === 'email' || saved === 'sms' || saved === 'gmail' || saved === 'outlook' || saved === 'yahoo' || saved === 'other-email') ? (saved as 'chat' | 'email' | 'sms' | 'gmail' | 'outlook' | 'yahoo' | 'other-email') : 'chat';
    } catch {
      return 'chat';
    }
  });

  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ url?: string; name?: string; type: 'image' | 'video' | 'file' | 'icon'; iconName?: string } | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasMoreOlderMessages, setHasMoreOlderMessages] = useState(true);
  const [paginationCursor, setPaginationCursor] = useState<string | undefined>(undefined);

  return {
    messages,
    setMessages,
    message,
    setMessage,
    subject,
    setSubject,
    displayName,
    setDisplayName,
    contactAvatarUrl,
    setContactAvatarUrl,
    contactId,
    setContactId,
    realId,
    setRealId,
    isBlankShellActive,
    setIsBlankShellActive,
    channel,
    setChannel,
    showOptionsModal,
    setShowOptionsModal,
    selectedImage,
    setSelectedImage,
    showImageModal,
    setShowImageModal,
    showAddModal,
    setShowAddModal,
    showTemplatesModal,
    setShowTemplatesModal,
    loading,
    setLoading,
    loadingOlderMessages,
    setLoadingOlderMessages,
    hasMoreOlderMessages,
    setHasMoreOlderMessages,
    paginationCursor,
    setPaginationCursor,
  };
};
